/**
 * Layout shell for unauthenticated public share pages.
 *
 * - Top nav: brand on the left, "Sign in" link on the right. No middle nav.
 * - Footer: brand attribution at the bottom of every share view.
 *
 * Used by both /share/file/$token (single file) and /share/folder/$token
 * (folder snapshot / live mirror).
 */
interface PublicShareLayoutProps {
  sharedBy: string | null;
  children: React.ReactNode;
  maxContentWidth?: "narrow" | "wide";
}

export function PublicShareLayout({
  sharedBy,
  children,
  maxContentWidth = "narrow",
}: PublicShareLayoutProps) {
  const contentClass = maxContentWidth === "wide" ? "max-w-5xl" : "max-w-3xl";
  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-900">Prominent Builders</span>
            {sharedBy && <span className="text-xs text-gray-500">· shared by {sharedBy}</span>}
          </div>
          <a
            href="/login"
            className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
          >
            Sign in
          </a>
        </div>
      </header>

      <main className={`mx-auto w-full flex-1 px-4 py-8 ${contentClass}`}>{children}</main>

      <footer className="border-t border-gray-200 bg-white">
        <div className="mx-auto flex max-w-5xl flex-col items-center justify-between gap-2 px-4 py-4 text-xs text-gray-500 sm:flex-row">
          <span>© {new Date().getFullYear()} Prominent Builders</span>
          <span>Built on Nymbl · Construction Management Platform</span>
        </div>
      </footer>
    </div>
  );
}
